import React from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import RelationName, { getRelationName } from '@/shared/components/RelationName';
import { mapAssignmentGroupName, mapResourceName } from '@/shared/services/ObjectDisplayService';
import { listItemMsg } from '@/shared/components/FormattedListValue';

import config from '@/config';
import DataCacheService from '@/shared/services/DataCacheService';
import AuthService from '@/shared/services/AuthService';
import { guid1 } from '@/shared/utils/guid';
import messages from '../reportingMessages';
import InForfaitCheckbox from './InForfaitCheckbox';
import HoursColumnValue from './HoursColumnValue';
import AmountColumnValue from './AmountColumnValue';
import QuantityColumnValue from './QuantityColumnValue';

const reportInfo = [
  {
    type: 4,
    title: messages.RegistrationsPerRelation,
    rootGroup: 1,
    url: `${config.appUrls.reporting}/api/RegistrationsPerRelation`,
    zendeskArticle: '5434847027730',
  },
  {
    type: 5,
    title: messages.RegistrationsPerResourceCategory,
    rootGroup: 4,
    url: `${config.appUrls.reporting}/api/RegistrationsPerResourceCategory`,
    zendeskArticle: '5434792789906',
  },
  {
    type: 6,
    title: messages.RegistrationsPerTaskTemplate,
    rootGroup: 6,
    url: `${config.appUrls.reporting}/api/RegistrationsPerTaskTemplate`,
    zendeskArticle: '5434760158098',
  },
  {
    type: 7,
    title: messages.RegistrationsPerUser,
    rootGroup: 8,
    url: `${config.appUrls.reporting}/api/RegistrationsPerUser`,
    zendeskArticle: '5434845825938',
  },
];
export const getReportTypeInfo = type => {
  const info = reportInfo.find(x => x.type === type);
  if (!info) throw new Error(`Unknown report type: ${type}`);
  return info;
};

const getRelationColumnValue = (row, intl) => {
  const {
    relationId: id,
    relationDisplayName: name,
    relationType: type,
    relationCrm: crm,
    relationAlias: alias,
    relationIsActive: isActive,
  } = row;

  const relation = {
    id,
    name,
    type,
    crm,
    alias,
    isActive,
  };
  return <RelationName relation={relation} />;
};
const getAssignmentColumnValue = (row, intl) => {
  const { assignmentName, assignmentTemplateName, assignmentTemplateId } = row;
  return mapAssignmentGroupName(assignmentTemplateId, assignmentTemplateName, assignmentName, intl);
};

const getResourceTypeColumnValue = (row, intl) => {
  const { resourceType } = row;
  return intl.formatMessage(listItemMsg('resourcetype', resourceType));
};

const getResourceCategoryColumnValue = (row, intl) => {
  const { categoryId, categoryName } = row;
  return intl.formatMessage(listItemMsg('resourcecategory', categoryId, categoryName));
};

const getResourceColumnValue = (row, intl) => {
  const {
    categoryId,
    categoryName,
    resourceId: id,
    resourceName: name,
    isDefaultResource: isDefault,
  } = row;

  return mapResourceName({ id, name, isDefault, categoryId, categoryName }, intl);
};
const getTaskTemplateColumnValue = (row, intl) => {
  const { taskTemplateId } = row;
  return taskTemplateId
    ? intl.formatMessage(listItemMsg('tasktemplate', taskTemplateId))
    : intl.formatMessage(messages.NotRegisteredOnTask);
};
const getSubTaskTemplateColumnValue = (row, intl) => {
  const { subTaskTemplateId } = row;
  return subTaskTemplateId
    ? // I'm currently unsure why, but 'tasktemplate' here seems to work as intended, even though we address subtasks
      intl.formatMessage(listItemMsg('tasktemplate', subTaskTemplateId))
    : intl.formatMessage(messages.NotRegisteredOnSubTask);
};
const getUserNameColumnValue = (row, intl) => {
  const { userId } = row;
  if (userId === guid1) return intl.formatMessage(messages.SystemUser);
  const users = DataCacheService.getAllUsers();
  if (!userId || !users) return null;

  const { name = null } = users.find(x => x.id === userId) || {};
  return name;
};
const getOwnCompanyColumnValue = row => row.name;
const getReferenceYearValue = (row, intl) => {
  const { label } = row;
  if (!label) return intl.formatMessage(messages.WithoutReferenceYear);
  return label;
};
const getSimpleGroupColumnValue = row => {
  const { label } = row;
  return label;
};
const getDetailColumnValue = (row, intl) => {
  const { registrationDate } = row;
  return !!registrationDate && intl.formatDate(registrationDate);
};
const getNameColumnAccessor = (row, intl) => {
  const { rowType } = row;
  switch (rowType) {
    case -1:
      return intl.formatMessage(messages.Totals);
    case 1:
      return getRelationColumnValue(row, intl);
    case 2:
      return getAssignmentColumnValue(row, intl);
    case 3:
      return getResourceTypeColumnValue(row, intl);
    case 4:
      return getResourceCategoryColumnValue(row, intl);
    case 5:
      return getResourceColumnValue(row, intl);
    case 6:
      return getTaskTemplateColumnValue(row, intl);
    case 8:
      return getUserNameColumnValue(row, intl);
    case 9:
    case 10:
    case 16:
    case 11:
      return getSimpleGroupColumnValue(row);
    case 12:
      return getReferenceYearValue(row, intl);
    case 13:
      return getOwnCompanyColumnValue(row);
    case 15:
      return getSubTaskTemplateColumnValue(row, intl);
    case 99:
      return getDetailColumnValue(row, intl);
    default:
      return `Unsupported type: ${rowType}`;
  }
};

export const getVisibleColumnsFromTotals = totals => {
  const {
    potentialQuantity,
    notBillableHours,
    notBillableQuantity,
    notBillableAmount,
    billedQuantity,
    billedInForfaitHours,
    billedInForfaitQuantity,
    billedInForfaitAmount,
    deletedHours,
    deletedQuantity,
    deletedAmount,
    neverInvoiceHours,
    neverInvoiceQuantity,
    neverInvoiceAmount,
    writtenOffHours,
    writtenOffQuantity,
    writtenOffAmount,
    internalCost,
  } = totals || {};
  const canViewPriceList = AuthService.hasPermission({
    functionality: 'Pricelist',
    privilege: 'read',
  });
  const canViewInternalPrice = AuthService.hasPermission({
    functionality: 'EmployeeInternalPrice',
    privilege: 'read',
  });
  const canViewDraftInvoices = AuthService.hasPermission({
    functionality: 'InvoiceDraft',
    privilege: 'read',
  });
  const canViewFinalInvoices = AuthService.hasPermission({
    functionality: 'InvoiceFinal',
    privilege: 'read',
  });
  return {
    potentialQuantity: (potentialQuantity ?? 0) > 0,
    notBillable: canViewPriceList && ((notBillableHours ?? 0) > 0 || (notBillableAmount ?? 0) > 0),
    notBillableQuantity: canViewPriceList && (notBillableQuantity ?? 0) > 0,
    billed: canViewFinalInvoices,
    billedQuantity: canViewFinalInvoices && (billedQuantity ?? 0) > 0,
    writtenOff: canViewFinalInvoices && ((writtenOffHours ?? 0) > 0 || (writtenOffAmount ?? 0) > 0),
    writtenOffQuantity: canViewFinalInvoices && (writtenOffQuantity ?? 0) > 0,
    inForfait:
      canViewFinalInvoices && ((billedInForfaitHours ?? 0) > 0 || (billedInForfaitAmount ?? 0) > 0),
    inForfaitQuantity: canViewFinalInvoices && (billedInForfaitQuantity ?? 0) > 0,
    deleted: canViewFinalInvoices && ((deletedHours ?? 0) > 0 || (deletedAmount ?? 0) > 0),
    deletedQuantity: canViewFinalInvoices && (deletedQuantity ?? 0) > 0,
    neverInvoice:
      canViewDraftInvoices && ((neverInvoiceHours ?? 0) > 0 || (neverInvoiceAmount ?? 0) > 0),
    neverInvoiceQuantity: canViewDraftInvoices && (neverInvoiceQuantity ?? 0) > 0,
    margin: canViewInternalPrice && canViewDraftInvoices && (internalCost ?? 0) > 0,
    priceListPermission: canViewPriceList,
    internalPricePermission: canViewInternalPrice,
    draftInvoicePermission: canViewDraftInvoices,
    finalInvoicePermission: canViewFinalInvoices,
  };
};
export const columnStatusShape = PropTypes.shape({
  potentialQuantity: PropTypes.bool.isRequired,
  notBillable: PropTypes.bool.isRequired,
  notBillableQuantity: PropTypes.bool.isRequired,
  billed: PropTypes.bool.isRequired,
  billedQuantity: PropTypes.bool.isRequired,
  inForfait: PropTypes.bool.isRequired,
  inForfaitQuantity: PropTypes.bool.isRequired,
  deleted: PropTypes.bool.isRequired,
  deletedQuantity: PropTypes.bool.isRequired,
  neverInvoice: PropTypes.bool.isRequired,
  neverInvoiceQuantity: PropTypes.bool.isRequired,
  margin: PropTypes.bool.isRequired,
  priceListPermission: PropTypes.bool.isRequired,
  internalPricePermission: PropTypes.bool.isRequired,
  draftInvoicePermission: PropTypes.bool.isRequired,
});
export const getColumns = (intl, showDetails, totals) => {
  const isVisible = getVisibleColumnsFromTotals(totals);
  const columns = [
    {
      Header: <FormattedMessage {...messages.Name} />,
      id: 'name',
      accessor: originalRow => getNameColumnAccessor(originalRow, intl),
      width: 2,
      minWidth: 100,
    },
  ];
  if (showDetails) {
    columns.push(
      {
        Header: <FormattedMessage {...messages.Remark} />,
        id: 'remark',
        accessor: ({ registrationRemark = '', registrationInternalRemark = '' }) =>
          `${registrationRemark} ${registrationInternalRemark}`,
      },
      {
        Header: <FormattedMessage {...messages.InvoiceStatus} />,
        id: 'invoiceStatus',
        accessor: ({ registrationInvoiceStatus }) =>
          registrationInvoiceStatus
            ? intl.formatMessage(
                listItemMsg('registrationinvoicestatus', registrationInvoiceStatus),
              )
            : null,
        isVisible: isVisible.finalInvoicePermission,
      },
      {
        Header: <FormattedMessage {...messages.InForfait} />,
        id: 'inForfait',
        accessor: 'registrationInForfait',
        Cell: InForfaitCheckbox,
        isVisible: isVisible.priceListPermission,
      },
    );
  }
  columns.push({
    Header: <FormattedMessage {...messages.Potential} />,
    id: 'potential',
    align: 'center',
    columns: [
      {
        Header: <FormattedMessage {...messages.Hours} />,
        id: 'potentialHours',
        accessor: 'potentialHours',
        Cell: HoursColumnValue,
        align: 'right',
      },
      {
        Header: <FormattedMessage {...messages.Quantity} />,
        id: 'potentialQuantity',
        accessor: 'potentialQuantity',
        Cell: QuantityColumnValue,
        align: 'right',
        isVisible: isVisible.potentialQuantity,
      },
      {
        Header: <FormattedMessage {...messages.Amount} />,
        id: 'potentialAmount',
        accessor: 'potentialAmount',
        Cell: AmountColumnValue,
        align: 'right',
        isVisible: isVisible.priceListPermission,
      },
      {
        Header: <FormattedMessage {...messages.InternalCost} />,
        id: 'internalCost',
        accessor: 'internalCost',
        Cell: AmountColumnValue,
        align: 'right',
        isVisible: isVisible.internalPricePermission,
      },
    ],
  });

  if (isVisible.notBillable) {
    columns.push({
      Header: <FormattedMessage {...messages.NotBillable} />,
      id: 'notBillable',
      align: 'center',
      columns: [
        {
          Header: <FormattedMessage {...messages.Hours} />,
          id: 'notBillableHours',
          accessor: 'notBillableHours',
          Cell: HoursColumnValue,
          align: 'right',
        },
        {
          Header: <FormattedMessage {...messages.Quantity} />,
          id: 'notBillableQuantity',
          accessor: 'notBillableQuantity',
          Cell: QuantityColumnValue,
          align: 'right',
          isVisible: isVisible.notBillableQuantity,
        },
        {
          Header: <FormattedMessage {...messages.Amount} />,
          id: 'notBillableAmount',
          accessor: 'notBillableAmount',
          Cell: AmountColumnValue,
          align: 'right',
        },
      ],
    });
  }
  if (isVisible.billed) {
    columns.push({
      Header: <FormattedMessage {...messages.Invoiced} />,
      id: 'invoiced',
      align: 'center',
      columns: [
        {
          Header: <FormattedMessage {...messages.Hours} />,
          id: 'billedHours',
          accessor: 'billedHours',
          Cell: HoursColumnValue,
          align: 'right',
        },
        {
          Header: <FormattedMessage {...messages.Quantity} />,
          id: 'billedQuantity',
          accessor: 'billedQuantity',
          Cell: QuantityColumnValue,
          align: 'right',
          isVisible: isVisible.billedQuantity,
        },
        {
          Header: <FormattedMessage {...messages.Amount} />,
          id: 'billedAmount',
          accessor: 'billedAmount',
          Cell: AmountColumnValue,
          align: 'right',
        },
      ],
    });
  }
  if (isVisible.inForfait) {
    columns.push({
      Header: <FormattedMessage {...messages.InvoicedInForfait} />,
      id: 'invoicedInForfait',
      align: 'center',
      columns: [
        {
          Header: <FormattedMessage {...messages.Hours} />,
          id: 'billedInForfaitHours',
          accessor: 'billedInForfaitHours',
          Cell: HoursColumnValue,
          align: 'right',
        },
        {
          Header: <FormattedMessage {...messages.Quantity} />,
          id: 'billedInForfaitQuantity',
          accessor: 'billedInForfaitQuantity',
          Cell: QuantityColumnValue,
          align: 'right',
          isVisible: isVisible.inForfaitQuantity,
        },
        {
          Header: <FormattedMessage {...messages.Amount} />,
          id: 'billedInForfaitAmount',
          accessor: 'billedInForfaitAmount',
          Cell: AmountColumnValue,
          align: 'right',
        },
      ],
    });
  }
  if (isVisible.deleted) {
    columns.push({
      Header: <FormattedMessage {...messages.Deleted} />,
      id: 'deleted',
      align: 'center',
      columns: [
        {
          Header: <FormattedMessage {...messages.Hours} />,
          id: 'deletedHours',
          accessor: 'deletedHours',
          Cell: HoursColumnValue,
          align: 'right',
        },
        {
          Header: <FormattedMessage {...messages.Quantity} />,
          id: 'deletedQuantity',
          accessor: 'deletedQuantity',
          Cell: QuantityColumnValue,
          align: 'right',
          isVisible: isVisible.deletedQuantity,
        },
        {
          Header: <FormattedMessage {...messages.Amount} />,
          id: 'deletedAmount',
          accessor: 'deletedAmount',
          Cell: AmountColumnValue,
          align: 'right',
        },
      ],
    });
  }
  if (isVisible.neverInvoice) {
    columns.push({
      Header: <FormattedMessage {...messages.NeverInvoice} />,
      id: 'neverInvoice',
      align: 'center',
      columns: [
        {
          Header: <FormattedMessage {...messages.Hours} />,
          id: 'neverInvoiceHours',
          accessor: 'neverInvoiceHours',
          Cell: HoursColumnValue,
          align: 'right',
        },
        {
          Header: <FormattedMessage {...messages.Quantity} />,
          id: 'neverInvoiceQuantity',
          accessor: 'neverInvoiceQuantity',
          Cell: QuantityColumnValue,
          align: 'right',
          isVisible: isVisible.neverInvoiceQuantity,
        },
        {
          Header: <FormattedMessage {...messages.Amount} />,
          id: 'neverInvoiceAmount',
          accessor: 'neverInvoiceAmount',
          Cell: AmountColumnValue,
          align: 'right',
        },
      ],
    });
  }
  if (isVisible.writtenOff) {
    columns.push({
      Header: <FormattedMessage {...messages.WrittenOffInternally} />,
      id: 'writtenOff',
      align: 'center',
      columns: [
        {
          Header: <FormattedMessage {...messages.Hours} />,
          id: 'writtenOffHours',
          accessor: 'writtenOffHours',
          Cell: HoursColumnValue,
          align: 'right',
        },
        {
          Header: <FormattedMessage {...messages.Quantity} />,
          id: 'writtenOffQuantity',
          accessor: 'writtenOffQuantity',
          Cell: QuantityColumnValue,
          align: 'right',
          isVisible: isVisible.writtenOffQuantity,
        },
        {
          Header: <FormattedMessage {...messages.Amount} />,
          id: 'writtenOffAmount',
          accessor: 'writtenOffAmount',
          Cell: AmountColumnValue,
          align: 'right',
        },
      ],
    });
  }
  if (isVisible.billed) {
    columns.push({
      Header: <FormattedMessage {...messages.InvoicedMinusPotential} />,
      id: 'invoicedMinusPotential',
      align: 'center',
      columns: [
        {
          Header: <FormattedMessage {...messages.Hours} />,
          id: 'invoicedMinusPotentialHours',
          accessor: 'deltaHours',
          Cell: HoursColumnValue,
          align: 'right',
        },
        {
          Header: <FormattedMessage {...messages.Quantity} />,
          id: 'invoicedMinusPotentialQuantity',
          accessor: 'deltaQuantity',
          Cell: QuantityColumnValue,
          align: 'right',
          isVisible: isVisible.billedQuantity,
        },
        {
          Header: <FormattedMessage {...messages.Amount} />,
          id: 'invoicedMinusPotentialAmount',
          accessor: 'deltaAmount',
          Cell: AmountColumnValue,
          align: 'right',
        },
      ],
    });
  }
  if (isVisible.margin) {
    columns.push({
      Header: <FormattedMessage {...messages.Margin} />,
      id: 'margin',
      align: 'right',
      columns: [
        {
          Header: <FormattedMessage {...messages.InvoicedMinusInternalCost} />,
          id: 'invoicedMinusInternalCost',
          accessor: 'margin',
          Cell: AmountColumnValue,
          align: 'right',
        },
      ],
    });
  }

  // Filter columns on isVisible columns
  return columns
    .map(x => {
      if (typeof x.isVisible !== 'undefined' && !x.isVisible) return null;
      if (typeof x.columns === 'undefined') return x;
      const subColumns = x.columns.filter(c => typeof c.isVisible === 'undefined' || !!c.isVisible);
      if (subColumns.length === 0) return null;
      return {
        ...x,
        columns: subColumns,
      };
    })
    .filter(Boolean);
};

export const defaultColumn = {
  // When using the useFlexLayout:
  minWidth: 30, // minWidth is only used as a limit for resizing
  width: 1, // width is used for both the flex-basis and flex-grow
  maxWidth: 200, // maxWidth is only used as a limit for resizing
};
